import React, { useState, useEffect } from "react";
import { Spin, message } from "antd";
import { FaSearchPlus, FaSearchMinus, FaArrowLeft, FaArrowRight } from "react-icons/fa";
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

import "../assets/css/pdfViewer.css";

// Set up the worker for react-pdf
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export default function InlinePdfViewer({ pdfUrl }) {
  // Fallback state
  const [pdfFallback, setPdfFallback] = useState(false);

  // PDF state
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(window.innerWidth < 768 ? 0.6 : 1.0);
  const [isRendering, setIsRendering] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setScale(0.6);
      } else {
        setScale(1.0);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPageNumber(1);
  };

  const onDocumentLoadError = (error) => {
    console.error("React-PDF failed to load:", error);
    setPdfFallback(true);
  };

  const onDocumentSourceError = (error) => {
    console.error("React-PDF source error:", error);
    setPdfFallback(true);
  };

  const handleZoomIn = () => setScale(prev => Math.min(prev + 0.2, 3.0));
  const handleZoomOut = () => setScale(prev => Math.max(prev - 0.2, 0.4));
  
  const handlePrevPage = () => {
    if (pageNumber > 1) {
      setIsRendering(true);
      setPageNumber(prev => prev - 1);
    }
  };
  
  const handleNextPage = () => {
    if (pageNumber < numPages) {
      setIsRendering(true);
      setPageNumber(prev => prev + 1);
    }
  };

  if (!pdfUrl) {
    return null;
  }

  return (
    <div className="inline-pdf-viewer mt-4" style={{ border: '1px solid #dee2e6', borderRadius: '8px', overflow: 'hidden' }}>
      {!pdfFallback ? (
        <>
          {/* Controls */}
          <div className="pdf-viewer-controls" style={{ backgroundColor: '#f8f9fa', padding: '10px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '15px', borderBottom: '1px solid #dee2e6' }}>
            <button className="btn btn-outline-secondary btn-sm" onClick={handleZoomOut} title="Zoom Out">
              <FaSearchMinus />
            </button>
            <span style={{ fontSize: '0.9rem', fontWeight: '500' }}>{Math.round(scale * 100)}%</span>
            <button className="btn btn-outline-secondary btn-sm" onClick={handleZoomIn} title="Zoom In">
              <FaSearchPlus />
            </button>
            
            <div style={{ width: '20px' }} /> {/* Spacer */}
            
            <button className="btn btn-outline-secondary btn-sm" onClick={handlePrevPage} disabled={pageNumber <= 1}>
              <FaArrowLeft />
            </button>
            <span style={{ fontSize: '0.9rem', fontWeight: '500' }}>
              Page {pageNumber} of {numPages || '--'}
            </span>
            <button className="btn btn-outline-secondary btn-sm" onClick={handleNextPage} disabled={pageNumber >= numPages}>
              <FaArrowRight />
            </button>
          </div>

          {/* PDF Document Viewer */}
          <div className="pdf-viewer-content" style={{ display: 'flex', justifyContent: 'center', padding: '20px', backgroundColor: '#e9ecef', overflowX: 'auto' }}>
            <Document
              file={pdfUrl}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              onSourceError={onDocumentSourceError}
              loading={<div style={{ padding: '40px', color: '#6c757d', fontWeight: '500' }}>Preparing document viewer...</div>}
              className="pdf-document-container"
              style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.1)', minHeight: '600px', display: 'flex', justifyContent: 'center', transition: 'all 0.3s ease' }}
            >
              {numPages && (
                <div style={{ opacity: isRendering ? 0.4 : 1, transition: 'opacity 0.3s ease-in-out' }}>
                  <Page
                    pageNumber={pageNumber}
                    scale={scale}
                    renderTextLayer={true}
                    renderAnnotationLayer={true}
                    className="pdf-page-wrapper"
                    onRenderSuccess={() => setIsRendering(false)}
                  />
                </div>
              )}
            </Document>
          </div>
        </>
      ) : (
        <div className="pdf-viewer-fallback-content" style={{ width: '100%', height: '80vh', minHeight: '600px' }}>
          <iframe 
            src={`https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(pdfUrl)}`} 
            width="100%" 
            height="100%" 
            style={{ border: 'none' }}
            title="Google Docs PDF Viewer Fallback"
          />
        </div>
      )}
    </div>
  );
}
